import React, { Fragment, useEffect, useMemo, useState } from "react";
import {Card, CardBody, Col, FormGroup, Label, Button, Modal, ModalHeader, ModalBody, Row} from "reactstrap";
import Select from "react-select";
import VariationJsx from "./variationJqx";
import {sizeMapper} from "./settings/map";
import SizeRatio from "./sizeRatioJqx";
import SizeRatioJqx from "./sizeRatioJqx";
import ColorJqx from "./colorJqx";
import ProductInfo from "./productInfo";
import {pricePercentage} from "./settings/calculation";
import { useDispatch, useSelector } from "react-redux";
import { getVariantMappingData } from "../../../store/prodcutSlice";
import { toast } from "react-toastify";

const Variation = (props) => {

    const {errors, register, setValue, trigger, getValues, productState,modules,productStates,currentUser, watch, hasMysteryTag} = props;
    const columnWidth=(currentUser.modules).includes('is_hide_rrp')?4:3;
    const hasPricePermission=(currentUser.permission).includes('Edit Prices');

    useEffect(() => {
        if (hasMysteryTag) {
            const currentColors = getValues('color') || [];
            const defaultSelected = currentColors.filter(item => item.name === "Default");

            // Only update if current selection is not already just "Default"
            if (defaultSelected.length !== currentColors.length) {
                setValue('color', defaultSelected);
                trigger('color');
            }
        }
    }, [hasMysteryTag]); // Only depend on hasMysteryTag


    const colorsOptions = hasMysteryTag
        ? productState.colors.filter(c => c.name === "Default") // only Default when mystery
        : productState.colors;
    const dispatch = useDispatch();

    // mapping modal state
      const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);

      // redux mapping state
      const variantMappingState = useSelector(({ product }) => product.product.variantMapping);
      const mappingData = variantMappingState?.data;

      const productId = getValues("id");

        const statusId = Number(watch("status_id"));
        const hasSellerLink = !!watch("has_seller_link"); // safer than === true

        // skip flag from form
        const skipSellerVariantMapping = !!watch("skip_seller_variant_mapping");

        // Map button only when NOT skipping
        const canShowMapButton =
        hasSellerLink === true && statusId !== 1 && !skipSellerVariantMapping;

        const canShowMappingControls = hasSellerLink === true && statusId !== 1;

      // watch reactive values for computed variants
      const watchedColors = watch('color');
      const watchedSizeConfig = watch('size_configuration');
      const watchedSellingType = watch('selling_type');

      // compute marketplace variants locally from current form state (Size/Matrix + Colors)
      const computedMarketplaceVariants = useMemo(() => {
        if (!hasSellerLink) return [];
        const code = getValues('code') || '';
        const colors = watchedColors || [];
        const sizes = watchedSizeConfig?.size_matrix
          ? sizeMapper(watchedSizeConfig.size_matrix)
          : (getValues('size') || []);
        const sellingTypes = watchedSellingType || [];
        const variants = [];
        colors.forEach((color) => {
          if (sellingTypes.find((x) => x.id === 1)) {
            sizes.forEach((size) => {
              variants.push({
                id: `${color.id}_${size.id}`,
                colour: color.name,
                colour_id: color.id,
                size: size.name,
                size_id: size.id,
                sku: `${code}-${color.name}-${size.name}`.toUpperCase().replaceAll(' ', ''),
              });
            });
          }
          if (sellingTypes.find((x) => x.id === 2)) {
            variants.push({
              id: `${color.id}_pack`,
              colour: color.name,
              colour_id: color.id,
              size: 'Pack',
              size_id: null,
              sku: `${code}-${color.name}-pack`.toUpperCase().replaceAll(' ', ''),
            });
          }
        });
        return variants;
      }, [watchedColors, watchedSizeConfig, watchedSellingType, hasSellerLink]);

      // local draft mapping (memory only)  key: "${colour_id}_${size_id|'pack'}" → seller_variant_id
      const [draftMap, setDraftMap] = useState({});

      // when modal opens, fetch seller variants from API; init draftMap from computed variants
      const openMappingModal = async () => {
        if (!productId) return;

        // do nothing if skip is ON
        if (skipSellerVariantMapping) return;

        setIsMappingModalOpen(true);

        const res = await dispatch(getVariantMappingData(productId));
        if (res?.error) {
          toast.error("Failed to load variant mapping data.", { position: toast.POSITION.TOP_RIGHT });
          return;
        }

        const payload = res.payload;

        // store meta using computed variants count
        setValue("variant_mapping_meta", {
          has_seller_link: payload?.has_seller_link === true,
          unmapped_variant_ids: [],
          marketplace_variants_count: computedMarketplaceVariants.length,
        });

        // initialize draft map
        const initial = {};

        // 1) from memory (hook-form) — new format: {colour_id, size_id, seller_store_product_variant_id}
        const mem = getValues("variant_mappings") || [];
        if (Array.isArray(mem) && mem.length > 0) {
            mem.forEach((m) => {
                if (m?.colour_id !== undefined && m?.seller_store_product_variant_id) {
                    const key = `${m.colour_id}_${m.size_id ?? 'pack'}`;
                    initial[key] = Number(m.seller_store_product_variant_id);
                }
            });
        } else {
            // 2) from backend — match API's existing mapping to computed variants by colour+size name
            (payload?.marketplace_variants || []).forEach((v) => {
                if (v?.mapped_seller_variant_id) {
                    const computed = computedMarketplaceVariants.find(
                        (c) => c.colour === v.colour && c.size === v.size
                    );
                    if (computed) {
                        initial[computed.id] = v.mapped_seller_variant_id;
                    }
                }
            });
        }

        setDraftMap(initial);

        // populate variant_mappings form value (in-memory, new format)
        const initialMappings = computedMarketplaceVariants
          .filter((v) => initial[v.id])
          .map((v) => ({
            colour_id: v.colour_id,
            size_id: v.size_id ?? null,
            seller_store_product_variant_id: Number(initial[v.id]),
          }));
        setValue("variant_mappings", initialMappings);
      };

      const closeMappingModal = () => setIsMappingModalOpen(false);

      // seller variant options from API
      const sellerOptions = useMemo(() => {
        return (mappingData?.seller_variants || []).map((s) => ({
          value: s.id,
          label: `${s.sku}${s.title ? " - " + s.title : ""}`,
          sku: s.sku,
        }));
      }, [mappingData]);

      // used seller IDs to prevent duplicates
      const usedSellerVariantIds = useMemo(() => {
        return new Set(Object.values(draftMap).filter(Boolean));
      }, [draftMap]);

      // compute unmapped keys from computed variants
      const computeUnmappedVariantIds = (computed, draft) => {
        return computed.filter((v) => !draft[v.id]).map((v) => v.id);
      };

      // save modal → update hook-form values (still no server call)
      const saveMappingToMemory = () => {
        if (!mappingData) return;

        // ignore save if skipping
        if (skipSellerVariantMapping) return;

        const unmapped = computeUnmappedVariantIds(computedMarketplaceVariants, draftMap);

        // store meta for main Save validation
        setValue("variant_mapping_meta", {
          has_seller_link: mappingData?.has_seller_link === true,
          unmapped_variant_ids: unmapped,
          marketplace_variants_count: computedMarketplaceVariants.length,
        });

        // store payload for backend update — {colour_id, size_id, seller_store_product_variant_id}
        const mappingsArr = computedMarketplaceVariants
          .filter((v) => draftMap[v.id])
          .map((v) => ({
            colour_id: v.colour_id,
            size_id: v.size_id ?? null,
            seller_store_product_variant_id: Number(draftMap[v.id]),
          }));
        setValue("variant_mappings", mappingsArr);

        if (unmapped.length > 0) {
          toast.error("Please map all variants before saving/publishing.", { position: toast.POSITION.TOP_RIGHT });
          return;
        }

        toast.success("Mapping saved temporarily. Now click Save/Save & Publish to save permanently.", { position: toast.POSITION.TOP_RIGHT });
        closeMappingModal();
      };

    useEffect(() => {
        if (skipSellerVariantMapping) {
            // close modal if open
            setIsMappingModalOpen(false);

            // clear local draft map
            setDraftMap({});

            // clear hook-form payload so backend receives nothing
            setValue("variant_mappings", []);

            // clear meta (optional but safe)
            setValue("variant_mapping_meta", {
            has_seller_link: hasSellerLink === true,
            unmapped_variant_ids: [],
            marketplace_variants_count: computedMarketplaceVariants.length,
            });
        }
    }, [skipSellerVariantMapping]);

    return (
        <Fragment>
            <Card>
                <CardBody>
                    <div className="ribbon ribbon-clip ribbon-danger ribbon-left mb-5">Variation Configurations</div>
                    {/* Skip mapping checkbox */}
                    {canShowMappingControls && (
                        <div className="d-flex flex-column align-items-end mb-2">
                            <FormGroup check className="mb-1">
                            <Label check style={{ cursor: "pointer" }}>
                                <input
                                type="checkbox"
                                className="mr-1"
                                checked={!!watch("skip_seller_variant_mapping")}
                                onChange={(e) => {
                                    setValue("skip_seller_variant_mapping", e.target.checked, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                    });
                                }}
                                />
                                Skip Seller Store Variants Mapping
                            </Label>
                            </FormGroup>

                            {skipSellerVariantMapping && (
                            <div className="text-danger small text-end">
                                Seller variant mapping is skipped for this product. Orders, Inventory, Prices won't be synced.
                            </div>
                            )}
                        </div>
                    )}


                    {/* Map button */}
                    {canShowMapButton && (
                        <div className="d-flex justify-content-end">
                            <Button color="primary" type="button" onClick={openMappingModal}>
                            Map Seller Variants
                            </Button>
                        </div>
                    )}
                    {/* Pending (status 1) products have no marketplace variants yet — the first Save creates them */}
                    {hasSellerLink === true && statusId === 1 && (
                        <div className="d-flex flex-column align-items-end">
                            <Button color="primary" type="button" disabled>
                            Map Seller Variants
                            </Button>
                            <div className="text-muted small mt-1">
                                Select colours and sizes and Save the product first; mapping is available once its variants are created.
                            </div>
                        </div>
                    )}

                    <div className='form-row mb-3'>
                        <Col md={columnWidth}>
                            <FormGroup>
                                <Label className="col-form-label">{(currentUser.modules).includes('is_hide_rrp')?"RRP":"Price"}</Label>
                                {hasPricePermission? <input name="price" type="number" {...register('price')} step="any"
                                       onBlur={(e)=>{
                                           setValue('value_change',e.target.value)
                                           // if(getValues('is_third_party_seller')){
                                           //    setValue('cost_per_item_price' ,pricePercentage(e.target.value,25))
                                           //     trigger('cost_per_item_price');
                                           // }
                                           trigger('value_change');
                                       }}
                                       className={`form-control ${errors.price ? 'is-invalid' : ''}`}/>:<div>{getValues('price')}</div>}
                                <div className="invalid-feedback">{errors.price?.message}</div>
                            </FormGroup>
                        </Col>
                        <Col md={columnWidth}>
                            <FormGroup>
                                <Label className="col-form-label">Sale Price</Label>
                                {hasPricePermission? <input name="sale_price" type="number" {...register('sale_price')} step="any"
                                       onBlur={(e)=>{
                                           setValue('value_change',e.target.value);
                                           trigger('value_change');
                                       }}
                                       className={`form-control ${errors.sale_price ? 'is-invalid' : ''}`}/>:<div>{getValues('sale_price')}</div>}
                                <div className="invalid-feedback">{errors.sale_price?.message}</div>
                            </FormGroup>
                        </Col>
                        <Col md={columnWidth}>
                            <FormGroup>
                                <Label className="col-form-label">Cost Per Item Price</Label>
                                {hasPricePermission?<input name="cost_per_item_price" type="number" {...register('cost_per_item_price')} step="any"
                                       className={`form-control ${errors.cost_per_item_price ? 'is-invalid' : ''}`}/>:<div>{getValues('cost_per_item_price')}</div>}
                                <div className="invalid-feedback">{errors.cost_per_item_price?.message}</div>
                            </FormGroup>
                        </Col>
                        {!(currentUser.modules).includes('is_hide_rrp') &&   <Col md={columnWidth}>
                            <FormGroup>
                                <Label className="col-form-label">RRP</Label>
                                <input name="rrp" type="number" {...register('rrp')} step="any"
                                       className={`form-control ${errors.rrp ? 'is-invalid' : ''}`}/>
                                <div className="invalid-feedback">{errors.rrp?.message}</div>
                            </FormGroup>
                        </Col>}
                    </div>
                    <div className="form-row mb-3">
                        <Col md="3">
                            <FormGroup>
                                <Label className="col-form-label">Selling Type</Label>
                                <Select getOptionLabel={(option) => option.name}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 100 }) }}
                                        defaultValue={getValues('selling_type')}
                                        isMulti
                                        getOptionValue={(option) => option.id} {...register("selling_type")}
                                        onChange={(e) => {
                                            setValue('selling_type', e);
                                            trigger('selling_type');
                                        }} className={` ${errors.selling_type ? 'is-invalid' : ''}`}
                                        options={productState.selling_types}/>

                                <div className="invalid-feedback">{errors.selling_type?.message}</div>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="col-form-label">Size/Matrix</Label>
                                <Select getOptionLabel={(option) => option.size_name}
                                        defaultValue={getValues('size_configuration')}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 100 }) }}
                                        getOptionValue={(option) => option.id} {...register("size_configuration")}
                                        onChange={(e) => {
                                            setValue('size_configuration', e);
                                            const newSizes = sizeMapper(e.size_matrix);
                                            setValue('size', newSizes);
                                            trigger('size_configuration');
                                        }} className={` ${errors.size_configuration ? 'is-invalid' : ''}`}
                                        options={productState.sizes}/>

                                <div className="invalid-feedback">{errors.size_configuration?.message}</div>
                            </FormGroup>
                        </Col>
                        <Col md="5">
                            <FormGroup>
                                <Label className="col-form-label">Colours</Label>
                                <Select getOptionLabel={(option) => option.name}
                                        value={getValues('color')}
                                        menuPortalTarget={document.body}
                                        isMulti={true}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 100 }) }}
                                        getOptionValue={(option) => option.id}
                                        onChange={(e) => {
                                            setValue('color', e);
                                            console.log(e);
                                            trigger('color');
                                        }} className={` ${errors.color ? 'is-invalid' : ''}`}
                                        options={colorsOptions}/>

                                <div className="invalid-feedback">{errors.color?.message}</div>
                            </FormGroup>
                        </Col>
                    </div>
                    {/*<ColorJqx setValue={setValue}*/}
                    {/*             trigger={trigger} productState={productState}*/}
                    {/*             getValues={getValues}*/}
                    {/*          memoData={{colour:getValues('color_value_change')}}*/}
                    {/*>*/}

                    {/*</ColorJqx>*/}
                    <div style={{color:"red"}}>{errors.color?.message}</div>

                </CardBody>
            </Card>
            <Card>
                <CardBody>
                    <div className="ribbon ribbon-clip ribbon-success ribbon-left mb-5">Inventory </div>
                    {hasMysteryTag && <div><p style={{color:"red", fontSize :"1rem", textAlign:'right'}}>This product has tag "Mystery", so you cannot make changes in quantity.</p></div>}
                    <div className="mt-3">
                        {(getValues('selling_type').length >1 || getValues('selling_type').find(x => x.id == 2)) && <SizeRatioJqx
                        getValues={getValues}
                        setValue={setValue}
                        trigger={trigger}
                        errors={errors}
                        memoData={{size:getValues('size_configuration')}}
                    ></SizeRatioJqx>}
                        <div style={{color:"red"}}>{errors.size?.message}</div>
                    </div>
                    <div className="mt-1">
                        {getValues('is_has_draft_product') &&<Col md={12}>
                            <ProductInfo  productStates={productStates} ></ProductInfo>
                        </Col>}
                    </div>
                    <div className="mt-3">
                        {getValues('id') &&  <VariationJsx
                        getValues={getValues}
                        watch={watch}
                        settingData={{color:getValues('color'),size:getValues('size'),selling_type:getValues('selling_type'),valueChange:getValues('value_change')}}
                        setValue={setValue}
                        trigger={trigger}
                        currentUser={currentUser}
                        productState={productState}
                        modules={modules}
                        hasMysteryTag={hasMysteryTag}
                    ></VariationJsx>}
                    </div>
                </CardBody>
            </Card>

            {/* Mapping Modal */}
            <Modal size="xl" isOpen={isMappingModalOpen} toggle={closeMappingModal}>
                <ModalHeader toggle={closeMappingModal}>Seller Variant Mapping</ModalHeader>
                <ModalBody>
                {skipSellerVariantMapping ? (
                    <div className="text-danger">
                    Mapping is skipped for this product (Skip Seller Store Variants Mapping is ON).
                    </div>
                ) : !mappingData ? (
                    <div>Loading...</div>
                ) : (
                    <div>
                    <Row className="mb-2">
                        <Col md={12}>
                        <strong>Map each marketplace variant to a seller variant. Duplicate seller SKU selection is not allowed.</strong>
                        </Col>
                    </Row>

                    {computedMarketplaceVariants.map((v) => {
                        const selected = draftMap[v.id] || null;

                        // options excluding already used IDs (except current selected)
                        const filteredOptions = sellerOptions.filter((opt) => {
                        if (opt.value === selected) return true;
                        return !usedSellerVariantIds.has(opt.value);
                        });

                        return (
                        <Row key={v.id} className="align-items-center mb-2">
                            <Col md={5}>
                            <div>
                                <div><b>Colour:</b> {v.colour || "-"}</div>
                                <div><b>Size:</b> {v.size || "-"}</div>
                                <div><b>SKU:</b> {v.sku || "-"}</div>
                            </div>
                            </Col>

                            <Col md={7}>
                            <Select
                                options={filteredOptions}
                                value={filteredOptions.find((x) => x.value === selected) || null}
                                isClearable
                                onChange={(opt) => {
                                const next = { ...draftMap };

                                if (!opt) {
                                    delete next[v.id];
                                } else {
                                    next[v.id] = opt.value;
                                }

                                setDraftMap(next);
                                }}
                                placeholder="Select seller SKU..."
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                            </Col>
                        </Row>
                        );
                    })}

                    <Row className="mt-3">
                        <Col md={12} style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <Button color="secondary" type="button" onClick={closeMappingModal}>
                            Cancel
                        </Button>
                        <Button color="primary" type="button" onClick={saveMappingToMemory}>
                            Save
                        </Button>
                        </Col>
                    </Row>

                    <Row className="mt-2">
                        <Col md={12} className="text-center">
                        <small>Note: This saves mapping temporarily. Use Save/Save & Publish to save permanently.</small>
                        </Col>
                    </Row>
                    </div>
                )}
                </ModalBody>
            </Modal>
        </Fragment>);

}
export default Variation;